import QuickFilterSelect from './QuickFilterSelect';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Box } from 'foundations-components/transitional/components/Box';
import UISelect from 'UIComponents/input/UISelect';
import { NumberInput } from 'foundations-components/transitional/components/NumberInput';
import { mapOperatorNameToOperatorType, mapOperatorTypeToOperator } from '../utils/dateDurationQuickFilterUtils';
import I18n from 'I18n';
import invariant from 'react-utils/invariant';
import { LESS } from 'customer-data-filters-ui/converters/crmSearch/FilterCRMSearchOperatorTypesType';
import { dateDurationOperatorOptions } from '../utils/dateDurationQuickFilterUtils';
import { FQF_TIME_UNITS, formatDurationToMilliseconds, formatDurationToUnit, getDefaultUnit } from '../../../utils/durationUtilities';
import { DATE, DATE_TIME } from 'customer-data-objects/property/PropertyTypes';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
export const DEFAULT_OPERATOR = LESS;
const getDurationOptions = isDate => {
  return isDate ? [{
    text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.days'),
    value: FQF_TIME_UNITS.days
  }] : [{
    text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.days'),
    value: FQF_TIME_UNITS.days
  }, {
    text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.hours'),
    value: FQF_TIME_UNITS.hours
  }, {
    text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.minutes'),
    value: FQF_TIME_UNITS.minutes
  }, {
    text: I18n.text('customerDataFiltersUI.quickFiltersBar.duration.time.seconds'),
    value: FQF_TIME_UNITS.seconds
  }];
};
const getDefaultState = () => ({
  operatorType: DEFAULT_OPERATOR,
  durationInput: 0,
  currentUnit: FQF_TIME_UNITS.days,
  isActive: false
});
const getStateFromCondition = condition => {
  if (condition) {
    const {
      value,
      name: operatorName,
      field
    } = condition;
    const operatorType = mapOperatorNameToOperatorType(operatorName);
    const isDate = (field === null || field === void 0 ? void 0 : field.type) === DATE;
    const initialUnit = isDate || !value ? FQF_TIME_UNITS.days : getDefaultUnit(value);
    const durationInput = formatDurationToUnit(value, initialUnit);
    return {
      operatorType,
      durationInput,
      currentUnit: initialUnit,
      isActive: true
    };
  }
  return getDefaultState();
};
const DateDurationQuickFilter = ({
  buttonProperties,
  condition,
  disableTruncation,
  isFirst,
  property,
  onValueChange
}) => {
  const initialState = getStateFromCondition(condition);
  const [durationInput, setDurationInput] = useState(initialState.durationInput);
  const [currentUnit, setCurrentUnit] = useState(initialState.currentUnit);
  const [operatorType, setOperatorType] = useState(initialState.operatorType);
  const [isActive, setIsActive] = useState(initialState.isActive);
  const [isOpen, setIsOpen] = useState(false);
  const isDate = property.type === DATE;
  const resetToDefaultState = useCallback(() => {
    setDurationInput(getDefaultState().durationInput);
    setOperatorType(getDefaultState().operatorType);
    setIsActive(getDefaultState().isActive);
  }, []);
  useEffect(() => {
    if (!condition) {
      resetToDefaultState();
    }
  }, [condition, resetToDefaultState]);
  const onApplyChanges = useCallback(() => {
    const Operator = mapOperatorTypeToOperator(operatorType);
    invariant(Operator, `Unable to find a operator for operator type "${operatorType}"`);
    const valueInMilliseconds = Math.floor(formatDurationToMilliseconds(durationInput, currentUnit));
    onValueChange(Operator.of(property).set('value', valueInMilliseconds));
    setIsOpen(false);
    setIsActive(true);
  }, [currentUnit, durationInput, onValueChange, operatorType, property]);
  const onClearChanges = useCallback(() => {
    if (condition) {
      onValueChange();
      resetToDefaultState();
    }
  }, [condition, onValueChange, resetToDefaultState]);
  const onToggleOpen = () => {
    setIsOpen(currentIsOpen => !currentIsOpen);
  };
  const onOperatorChange = evt => {
    const newOperator = evt.target.value;
    setOperatorType(newOperator);
  };
  const onDurationInputChange = evt => {
    /*
     * When UINumberInput uses precision: 0, it only resets the value to an integer onBlur.
     * This ensures that the value is an integer without onBlur having been called
     */
    let value = evt.target.value;
    if (!Number.isInteger(value)) {
      value = Math.floor(value);
    }
    setDurationInput(value);
  };
  const onDurationUnitChange = evt => {
    setCurrentUnit(evt.target.value);
  };
  const operatorOptions = useMemo(() => {
    const key = isDate ? DATE : DATE_TIME;
    return dateDurationOperatorOptions[key].map(({
      langToken,
      symbol,
      value
    }) => ({
      text: I18n.text(langToken),
      dropdownText: `${symbol} ${I18n.text(langToken)}`,
      value
    }));
  }, [isDate]);
  return /*#__PURE__*/_jsx(QuickFilterSelect, {
    property: property,
    buttonProperties: buttonProperties,
    buttonText: property.displayName,
    condition: condition,
    disableTruncation: disableTruncation,
    isActive: isActive,
    isFirst: isFirst,
    isOpen: isOpen,
    popoverContent: {
      body: /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(TFlex, {
          paddingBlockEnd: "200",
          children: /*#__PURE__*/_jsx(Box, {
            children: /*#__PURE__*/_jsx(UISelect, {
              "data-selenium-test": "DateDurationQuickFilter-comparator",
              menuWidth: 'auto',
              onChange: onOperatorChange,
              options: operatorOptions,
              value: operatorType
            })
          })
        }), /*#__PURE__*/_jsxs(TFlex, {
          align: "center",
          children: [/*#__PURE__*/_jsx(Box, {
            grow: 1,
            paddingInlineEnd: "300",
            children: /*#__PURE__*/_jsx(NumberInput, {
              "data-selenium-test": "DateDurationQuickFilter-value"
              // @ts-expect-error UINumberInput hasn't been properly migrated yet
              ,
              onChange: onDurationInputChange,
              value: durationInput,
              precision: 0,
              formatter: value => value != null ? I18n.formatNumber(value, {
                precision: 0
              }) : ''
            })
          }), /*#__PURE__*/_jsx(Box, {
            grow: 2,
            children: /*#__PURE__*/_jsx(UISelect, {
              "data-selenium-test": "DateDurationQuickFilter-unit",
              onChange: onDurationUnitChange,
              options: getDurationOptions(isDate),
              value: currentUnit,
              readOnly: isDate
            })
          })]
        })]
      }),
      footer: /*#__PURE__*/_jsxs(TFlex, {
        align: "center",
        direction: "row",
        justify: "between",
        wrap: "nowrap",
        children: [/*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "DateDurationQuickFilter-apply",
          size: "small",
          use: "tertiary",
          onClick: onApplyChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.number.actions.apply"
          })
        }), /*#__PURE__*/_jsx(Button, {
          "data-selenium-test": "DateDurationQuickFilter-clear",
          size: "small",
          use: "tertiary-light",
          onClick: onClearChanges,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "customerDataFiltersUI.quickFiltersBar.number.actions.clear"
          })
        })]
      })
    },
    onToggleOpen: onToggleOpen,
    onValueChange: onValueChange,
    width: "350px"
  });
};
export default DateDurationQuickFilter;